import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, model } from '@angular/core';
import { Router } from '@angular/router';
import { SearchResultRenderer, SOFT_SHELL_SEARCH_DISMISS } from '@softapps/core';
import { IconComponent, translator } from '@softline/ui-core';
import { documentTypeName } from '../../helper/document-type-label';
import { DocumentTypeService } from '../../services/document-type.service';
import { SalesDocumentSearchResult } from '../../services/sales-document-search.provider';

/** Last-resort labels: the search payload carries only the enum name, so the
 *  wording comes from /document/query/types — this map covers the window
 *  before that list has loaded. */
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  SALES_ORDER: 'Auftrag',
  OFFER: 'Offert',
  BLANKET_ORDER: 'Rahmenauftrag',
  DELIVERY_NOTE: 'Lieferschein',
  INVOICE: 'Rechnung',
  WORK_ORDER: 'Arbeitsauftrag',
  COMMISSION_NOTE: 'Kommissionierschein',
};

@Component({
  selector: 'soft-sales-document-search-result',
  imports: [IconComponent, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let doc = result();
    <div
      class="flex items-center gap-2 w-full cursor-pointer px-1 py-1.5 rounded-md"
      [class.bg-hover]="highlighted()"
      (click)="open(doc)"
    >
      <span
        class="flex items-center justify-center min-w-8 min-h-8 rounded-md bg-surface-secondary text-light shrink-0"
      >
        <soft-icon name="fa-regular fa-file-lines" class="text-sm" />
      </span>
      <div class="flex flex-col min-w-0 text-start">
        <span class="block truncate text-sm font-semibold leading-snug">
          {{ documentTypeLabel(doc) }} {{ doc.documentNumber }}
        </span>
        <span class="block text-xs text-light leading-normal">
          @if (doc.customerNumber) {
            {{ doc.customerNumber }} —
          }
          {{ doc.customerName }}
          @if (doc.documentDate) {
            · {{ doc.documentDate | date: 'dd.MM.yyyy' }}
          }
        </span>
      </div>
    </div>
  `,
  host: {
    class: 'block w-full min-w-0 overflow-hidden',
    '(keydown.enter)': 'open(this.result())',
  },
})
export class SalesDocumentSearchResultComponent extends SearchResultRenderer<SalesDocumentSearchResult> {
  override readonly result = input.required<SalesDocumentSearchResult>();
  override readonly highlighted = input(false);
  override readonly disabled = model(false);

  private readonly router = inject(Router);
  private readonly dismiss = inject(SOFT_SHELL_SEARCH_DISMISS, { optional: true });
  // Optional: the shell renders this outside the sales routes too.
  private readonly documentTypes = inject(DocumentTypeService, { optional: true });
  private readonly t = translator();
  private readonly translate = (key: string): string => this.t(key)();

  documentTypeLabel(doc: SalesDocumentSearchResult): string {
    const known = this.documentTypes
      ?.all()
      ?.find((t) => t.type === doc.documentType);
    if (known) return documentTypeName(known, this.translate);
    return DOCUMENT_TYPE_LABELS[doc.documentType] ?? doc.documentType;
  }

  open(doc: SalesDocumentSearchResult): void {
    if (!doc?.id) return;
    this.dismiss?.();
    void this.router.navigate(['/sales/document', doc.id]);
  }
}
